import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { formatConversionType, formatDate } from '../utils/formatters';
import { FiSearch, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useDarkMode } from '../context/DarkModeContext';

const ConversionHistory = ({ history, clearHistory, onClose }) => {
  const { darkMode } = useDarkMode();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredAndSortedHistory = useMemo(() => {
    return history
      .filter(item => 
        item.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatConversionType(item.conversionType).toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (sortBy === 'date') {
          return sortOrder === 'asc' 
            ? new Date(a.timestamp) - new Date(b.timestamp)
            : new Date(b.timestamp) - new Date(a.timestamp);
        } else {
          return sortOrder === 'asc'
            ? a.fileName.localeCompare(b.fileName)
            : b.fileName.localeCompare(a.fileName);
        }
      });
  }, [history, searchTerm, sortBy, sortOrder]);

  const paginatedHistory = filteredAndSortedHistory.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredAndSortedHistory.length / itemsPerPage);

  const toggleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 rounded-xl shadow-lg border border-gray-100 dark:border-gray-800 transition-colors duration-300">
      <h2 className="text-2xl font-bold mb-4">Conversion History</h2>
      <div className="mb-4 flex justify-between items-center">
        <div className="relative flex-grow mr-2">
          <input
            type="text"
            placeholder="Search conversions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 pl-8 rounded bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors"
          />
          <FiSearch className="absolute left-2 top-3 text-gray-400 dark:text-gray-500" />
        </div>
        <button
          onClick={clearHistory}
          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          Clear History
        </button>
      </div>

      {filteredAndSortedHistory.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">No conversion history available.</p>
      ) : (
        <div className="flex-grow overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-inherit">
              <tr>
                <th className="cursor-pointer text-left p-2" onClick={() => toggleSort('fileName')}>
                  File Name {sortBy === 'fileName' && (sortOrder === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                </th>
                <th className="text-left p-2">Conversion</th>
                <th className="cursor-pointer text-left p-2" onClick={() => toggleSort('date')}>
                  Date {sortBy === 'date' && (sortOrder === 'asc' ? <FiChevronUp className="inline" /> : <FiChevronDown className="inline" />)}
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedHistory.map((item, index) => (
                <tr key={index} className="hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <td className="p-2 truncate max-w-[150px]">{item.fileName}</td>
                  <td className="p-2">{formatConversionType(item.conversionType)}</td>
                  <td className="p-2">{formatDate(item.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-between items-center">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ConversionHistory;